import { Component, Input, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CerrixTextfieldComponent } from "@app/shared/components/cerrix-textinput/cerrix-textfield/cerrix-textfield.component";
import { CerrixTextAreaComponent } from "@app/shared/components/cerrix-textinput/cerrix-textarea/cerrix-textarea.component";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { ControlExecutionPermissionModel } from "@app/controls/control-execution/control-execution-detail/models/control-execution-permission-model";
import { TaskSeriesModel } from "@app/tasks/models/task-series-model";
import { TaskSeriesDataService } from "@app/tasks/services/task-series-data.service";
import {
    AgendaService,
    DayService,
    MonthAgendaService,
    MonthService,
    TimelineMonthService,
    TimelineViewsService,
    WeekService,
    WorkWeekService,
} from "@syncfusion/ej2-angular-schedule";
import {
    CerrixRecurrenceComponent,
    RecurrenceValidationError,
} from "@app/shared/components/cerrix-recurrence/cerrix-recurrence.component";
import { RecurrenceViewModel } from "@app/shared/components/cerrix-recurrence/recurrence-view-model";
import { RecurrenceFrequency } from "@app/shared/components/cerrix-recurrence/cerrix-recurrence-frequency.enum";
import { NotificationService } from "@app/mailmessages/service/notification.service";
import { ModuleType } from "@enums/ModuleType";
import { TaskType } from "@app/tasks/enums/TaskType";
import { StandingDataHelper } from "@app/standingdata/shared/standingdata.helper";
import { injectTranslation, provideTranslationScope, TranslatePipe } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/tasks/i18n/scope";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "task-series-detail-main",
    templateUrl: "./task-series-detail-main.component.html",
    styleUrls: ["./task-series-detail-main.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    providers: [
        DayService,
        WeekService,
        WorkWeekService,
        MonthService,
        AgendaService,
        MonthAgendaService,
        TimelineViewsService,
        TimelineMonthService,
    ],
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        NgSelectModule,
        MatTooltipModule,
        CerrixTextfieldComponent,
        CerrixTextAreaComponent,
        CerrixRecurrenceComponent,
    ],
})
export class TaskSeriesDetailMainComponent implements OnInit {
    private _ds = inject(TaskSeriesDataService);
    private _notificationService = inject(NotificationService);

    @Input() cerrixTab: TabModel;
    @Input() model: TaskSeriesModel;
    @Input() permissions: ControlExecutionPermissionModel;
    @ViewChild("recurrenceComponent")
    private recurrenceComponent: CerrixRecurrenceComponent;

    private _translation = injectTranslation(scope);

    backupModel: TaskSeriesModel;

    formGroup: FormGroup;
    validResponsibles: IdNameCombination[];
    protected validReviewers: IdNameCombination[];
    validNotificationPeriods: IdNameCombination[];
    linkedTypes: IdNameCombination[];
    recurrenceViewModel: RecurrenceViewModel;

    taskScoreOptions: { ID: number; Name: string }[] = [];

    async ngOnInit(): Promise<void> {
        this.backupModel = JSON.parse(JSON.stringify(this.model));
        this.recurrenceViewModel = this.ConvertTaskSeriesRecurrenceToViewModel();

        await this._translation.ready();

        this.taskScoreOptions = [
            { ID: 1, Name: this._translation.t("taskScoreRequired") },
            { ID: 2, Name: this._translation.t("taskScoreOptional") },
            { ID: 3, Name: this._translation.t("taskScoreNotVisible") },
        ];
        this.linkedTypes = [{ ID: TaskType.ControlExecution, Name: this._translation.t("controlExecution") }];

        this.validResponsibles = await firstValueFrom(
            this._ds.getResponsibles(this.model.responsibleIds)
        );
        this.validReviewers = await firstValueFrom(this._ds.getReviewers(this.model.reviewerIds));
        this.validNotificationPeriods = (
            await firstValueFrom(this._notificationService.getNotificationStandingData(ModuleType.Tasks))
        ).periods;

        await this.initFormGroup();

        this.cerrixTab.showLoader = false;
    }

    async initFormGroup() {
        const responsibleIdsFormControl = new FormControl(
            this.model.responsibleIds,
            Validators.required
        );
        const reviewerIdsFormControl = new FormControl(this.model.reviewerIds);
        this.formGroup = new FormGroup({
            name: new FormControl(this.model.name, Validators.required),
            description: new FormControl(this.model.description),
            responsibleIds: responsibleIdsFormControl,
            reviewerIds: reviewerIdsFormControl,
            linkedTypes: new FormControl(this.model.linkedTypes, Validators.required),
            notificationPeriodIds: new FormControl(this.model.notificationPeriodIds),
            taskScoreOption: new FormControl(this.model.taskScoreOption, Validators.required),
        });
        this.formGroup.valueChanges.subscribe(() => {
            FormValidationHelper.mapToModel(this.formGroup, this.model);
        });

        // User can't be responsible and reviewer at the same time
        responsibleIdsFormControl.valueChanges.subscribe((v) => {
            this.validReviewers = StandingDataHelper.getOptionsDisabledByIds(
                this.validReviewers,
                v
            );
        });
        reviewerIdsFormControl.valueChanges.subscribe((v) => {
            this.validResponsibles = StandingDataHelper.getOptionsDisabledByIds(
                this.validResponsibles,
                v
            );
        });

        this.validResponsibles = StandingDataHelper.getOptionsDisabledByIds(
            this.validResponsibles,
            this.model.reviewerIds
        );
        this.validReviewers = StandingDataHelper.getOptionsDisabledByIds(
            this.validReviewers,
            this.model.responsibleIds
        );
    }

    validateRecurrence(): RecurrenceValidationError | null {
        return this.recurrenceComponent.validate();
    }

    recurrenceErrorText(error: RecurrenceValidationError): Promise<string> {
        return this.recurrenceComponent.errorText(error);
    }

    private ConvertTaskSeriesRecurrenceToViewModel(): RecurrenceViewModel {
        const modelRecurrence = this.model.recurrence;
        let viewModel: RecurrenceViewModel = Object.assign(
            {},
            new RecurrenceViewModel(),
            modelRecurrence
        );
        if (
            modelRecurrence.frequency &&
            (modelRecurrence.frequency === RecurrenceFrequency.Monthly ||
                modelRecurrence.frequency === RecurrenceFrequency.Yearly)
        ) {
            if (
                modelRecurrence.frequency === RecurrenceFrequency.Monthly &&
                modelRecurrence.isWorkDay
            ) {
                viewModel.radioSelection = "WorkDay";
            }
            if (!modelRecurrence.byMonthDay || modelRecurrence.byMonthDay == 0) {
                viewModel.otherByDay = modelRecurrence.byDay;
                viewModel.otherByDayType = modelRecurrence.bySetPos;
                viewModel.radioSelection = "Other";
            }
        } else {
            viewModel.radioSelection = "Day";
        }
        return viewModel;
    }
}
